/**
 * Inicio de sesión.
 *
 * Requiere conexión: la sesión offline SOLO puede nacer de una validación en línea.
 * Tras iniciarla, la aplicación descarga los datos autorizados y ya puede operar sin red.
 */

import { useState } from 'react';
import type { LoginResponse } from '@senm/contracts';
import { ApiRequestError, apiRequest } from '../api/client.js';
import { getInstallationId, saveSession, type StoredSession } from '../local/session.js';
import { synchronize } from '../sync/engine.js';

export function LoginPage(props: {
  motivo: 'VENCIDA' | null;
  aviso: { tono: string; texto: string } | null;
  conexion: string;
  onIniciada: (session: StoredSession) => Promise<void>;
}): JSX.Element {
  const [usuario, setUsuario] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    if (enviando) return; // prevención de doble envío
    setEnviando(true);
    setError(null);

    try {
      const installationId = await getInstallationId();
      const respuesta = await apiRequest<LoginResponse>('/v1/auth/login', {
        anonymous: true,
        body: {
          username: usuario.trim().toLowerCase(),
          password: contrasena,
          installationId,
          installationLabel: navigator.userAgent.slice(0, 60),
        },
      });

      const session: StoredSession = { ...respuesta, lastRevalidatedAt: new Date().toISOString() };
      await saveSession(session);
      // Primera descarga de datos autorizados: sin esto no habría nada offline.
      await synchronize(new Date().toISOString());
      await props.onIniciada(session);
    } catch (problema) {
      if (problema instanceof ApiRequestError) {
        setError(
          problema.isNetworkFailure
            ? 'No hay conexión con el servidor. Para iniciar sesión por primera vez necesita conexión.'
            : problema.message,
        );
      } else {
        setError('No fue posible iniciar sesión.');
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <main>
      <div className="acceso">
        <h1>FOLIO</h1>
        <p className="ayuda">Cobros y colegiaturas, con o sin conexión.</p>

        {props.motivo === 'VENCIDA' && (
          <div className="aviso-caja" role="status">
            Su acceso sin conexión venció. Vuelva a iniciar sesión para continuar.
          </div>
        )}
        {props.aviso !== null && (
          <div className={`aviso-caja ${props.aviso.tono === 'error' ? 'error' : ''}`} role="status">
            {props.aviso.texto}
          </div>
        )}
        {props.conexion === 'SIN_CONEXION' && (
          <div className="aviso-caja" role="status">
            Sin conexión. El primer inicio de sesión en este dispositivo requiere conexión.
          </div>
        )}

        <form onSubmit={(evento) => void enviar(evento)} className="tarjeta">
          <label>
            <span>Usuario</span>
            <input
              value={usuario}
              onChange={(evento) => setUsuario(evento.target.value)}
              autoComplete="username"
              required
              autoFocus
              aria-invalid={error !== null}
            />
          </label>
          <label>
            <span>Contraseña</span>
            <input
              type="password"
              value={contrasena}
              onChange={(evento) => setContrasena(evento.target.value)}
              autoComplete="current-password"
              required
              aria-invalid={error !== null}
            />
          </label>

          {error !== null && (
            <div className="aviso-caja error" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="boton primario" disabled={enviando}>
            {enviando ? 'Verificando…' : 'Entrar'}
          </button>
        </form>

        <p className="ayuda">
          ¿Olvidó su contraseña? Solicite a Administración un restablecimiento; recibirá un enlace de
          un solo uso con vigencia corta.
        </p>
      </div>
    </main>
  );
}
